import React, { useState, useMemo, useEffect } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";

const AutoCompleteFieldForTaxDetails = ({
  index,
  items,
  productSections,
  setProductSections,
  calculateFinalPrice,
}) => {
  const [search, setSearch] = useState(productSections[index].hsnNumber || "");
  const { t } = useTranslation();
  const data = useMemo(() => {
    // Show suggestions when search has at least 3 characters (numbers or otherwise)
    if (search && search.length >= 3) {
      return items.filter((obj) =>
        obj.hsnCode.toLowerCase().includes(search.toLowerCase())
      );
    }
    return [];
  }, [search, items]);
  useEffect(() => {
    const hsnValue = productSections[index]?.hsnNumber || "";
    setSearch(hsnValue);
  }, [productSections[index].hsnNumber]);

  const handleItemSelect = (event, newValue) => {
    if (!newValue) return;
    setSearch("");

    setProductSections((prevSections) => {
      const updatedSections = [...prevSections];
      updatedSections[index] = {
        ...updatedSections[index],
        hsnNumber: newValue.hsnCode
          .replace(/\s+/g, "")
          .replace(/,+/g, ",")
          .trim(),
        cgst: newValue.CGST.replace("%", ""),
        sgst: newValue.SGST.replace("%", ""),
        igst: newValue.IGST.replace("%", ""),
      };
      calculateFinalPrice(index, updatedSections);

      return updatedSections;
    });
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearch(value);

    // Update the hsnNumber in state as user types
    setProductSections((prevSections) => {
      const updatedSections = [...prevSections];
      updatedSections[index] = {
        ...updatedSections[index],
        hsnNumber: value,
      };
      return updatedSections;
    });
  };

  return (
    <Autocomplete
      freeSolo
      options={data}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.hsnCode
      }
      inputValue={search}
      onInputChange={(event, newInputValue) => setSearch(newInputValue)}
      onChange={(event, newValue) => handleItemSelect(event, newValue)}
      disableClearable
      sx={{ width: "100%" }}
      renderInput={(params) => (
        <TextField
          {...params}
          type='text'
          name={`productSections[${index}].hsnNumber`}
          placeholder='HSN code'
          className='w-full mt-1'
          value={productSections[index].hsnNumber || ""}
          onChange={handleInputChange}
          size='small'
          fullWidth
          variant='outlined'
          InputProps={{
            ...params.InputProps,
            className:
              "border-2 p-1 rounded-lg border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-800 text-black dark:text-white placeholder-gray-400",
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: 33,
              fontSize: 14,
              padding: "2px 8px",
            },
            "& .MuiInputLabel-root": {
              fontSize: 12,
            },
          }}
        />
      )}
      renderOption={(props, option) => (
        <li
          {...props}
          key={option.hsnCode}
          style={{ fontSize: "12px", padding: "4px 10px" }}
        >
          <div>
            <strong>HSN:</strong> {option.hsnCode}
            <br />
            <small>
              {t("TAX.CGST")}: {option.CGST}%, {t("TAX.SGST")}: {option.SGST}%,
              {t("TAX.IGST")}: {option.IGST}%
            </small>
          </div>
        </li>
      )}
      filterOptions={(options, state) => options} // Bypass default filtering since we do it in useMemo
    />
  );
};

export default AutoCompleteFieldForTaxDetails;
